/**
 * Reading mode for a markdown document (RFC 0297 §5a): the same prose set to
 * be read rather than edited, with document links and embeds resolved, and a
 * Mermaid block drawn. Editing is the shipped editor, `@mod/editor`, mounted
 * by `DocumentView` through the host-realm path (RFC 0314 S7), or whichever
 * installed app claims `text/markdown`.
 */

import { isValidDocumentId } from '@automerge/automerge-repo/slim'
import { useMemo } from 'react'
import ReactMarkdown, { type Components } from 'react-markdown'
import remarkGfm from 'remark-gfm'
import type { Subject } from '../../shell/window/subject'
import {
  DocumentChip,
  DocumentEmbed,
  type LinkOpenTarget,
  linkOpenTarget,
  loneLinkHref,
  useDocumentResolver,
  useEmbedded,
} from './DocumentLink'
import { MermaidDiagram, mermaidChartFrom } from './MermaidDiagram'

/**
 * A mermaid fence renders as a DIAGRAM instead of a code block. Intercepted at
 * the `pre` level (react-markdown wraps a fenced block in `<pre><code
 * class="language-mermaid">`), so a mermaid block is replaced whole and every
 * other code block renders normally.
 *
 * Moved here from DocumentView with the markdown branch it belongs to.
 */
const MARKDOWN_COMPONENTS: Components = {
  pre({ children, ...rest }) {
    const chart = mermaidChartFrom(children)
    if (chart !== null) return <MermaidDiagram chart={chart} />
    return <pre {...rest}>{children}</pre>
  },
}

/**
 * The renderers, plus the two that know about project documents (RFC 0297).
 *
 * Built per render rather than beside `MARKDOWN_COMPONENTS`, because resolving
 * an href needs the catalog and opening one needs the host's callback, and
 * neither exists at module scope.
 *
 * A LONE LINK IS A BLOCK, an inline one is a chip. The rule is the paragraph's
 * shape rather than a syntax of its own: markup would be a second thing to
 * learn and would show as raw text to any reader that had not learned it, which
 * is the trade RFC 0292 §3a already refused for mentions.
 */
export function markdownComponents({
  resolve,
  embedded,
  projectId,
  documentId,
  onOpenDocument,
  onOpenUrl,
}: {
  /** href to what it opens, or null. `useDocumentResolver` in the app. */
  resolve: (href: string | undefined) => Subject | null
  /** Already inside an embed, so every link here is a chip. */
  embedded: boolean
  projectId: string
  documentId: string
  onOpenDocument?: (id: string, target?: LinkOpenTarget) => void
  onOpenUrl?: (url: string, target?: LinkOpenTarget) => void
}): Components {
  return {
    ...MARKDOWN_COMPONENTS,
    a({ href, children, ...rest }) {
      const subject = resolve(href)
      if (!subject) {
        if (href && isValidDocumentId(href)) {
          return (
            <span className="text-muted-foreground" title="No access">
              {children} <span className="text-xs">No access</span>
            </span>
          )
        }
        return (
          <a
            href={href}
            {...rest}
            onClick={(event) => {
              if (!event.metaKey || !href || !onOpenUrl) return
              event.preventDefault()
              onOpenUrl(href, linkOpenTarget(event))
            }}
          >
            {children}
          </a>
        )
      }
      return (
        <DocumentChip subject={subject} onOpen={onOpenDocument}>
          {children}
        </DocumentChip>
      )
    },
    p({ children, node, ...rest }) {
      const subject = resolve(loneLinkHref(node))
      // A document that embedded ITSELF would mount a second copy of the
      // page it is drawn on, so it stays a chip. Same for anything inside an
      // embed already.
      if (subject && !embedded && subject.id !== documentId) {
        return (
          <DocumentEmbed
            subject={subject}
            projectId={projectId}
            onOpen={onOpenDocument}
          />
        )
      }
      return <p {...rest}>{children}</p>
    },
  }
}

export function MarkdownReader({
  documentId,
  projectId,
  content,
  onOpenDocument,
  onOpenUrl,
}: {
  documentId: string
  projectId: string
  /** The prose. Already hydrated by the caller. */
  content: string
  /** RFC 0297 §5a: what a link to a project document does when clicked. */
  onOpenDocument?: (id: string, target?: LinkOpenTarget) => void
  /** Modified web links use the same shell placement rules as documents. */
  onOpenUrl?: (url: string, target?: LinkOpenTarget) => void
}) {
  const resolve = useDocumentResolver(projectId)
  const embedded = useEmbedded()
  const components = useMemo(
    () =>
      markdownComponents({
        resolve,
        embedded,
        projectId,
        documentId,
        onOpenDocument,
        onOpenUrl,
      }),
    [resolve, embedded, projectId, documentId, onOpenDocument, onOpenUrl],
  )

  // NO CONTAINER OF ITS OWN. `DocumentView` wraps every document branch in
  // `DocumentComments`, which is the scroll container and the positioning
  // parent for a comment popover.
  return (
    <article className="prose-mod prose-mod-preview mx-auto w-full max-w-3xl px-8 py-12">
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {content}
      </ReactMarkdown>
    </article>
  )
}
